import Router from 'next/router';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { htmlToPlainText } from 'shared-helpers/common';
import { pathCanBeJoined } from 'lib/paths';
import {
  CategoryBadges,
  LinkButton,
  UserPathStepProgress
} from 'components/common';
import { CardProgressStatus } from 'components/common/tasks';
import {
  PathPicture,
  PathsTileHeader,
  PathsTileJoinPath,
  PathsTileSteps
} from '.';

import type {
  CompetencyInProduct,
  Path,
  PathStep,
  PathStepProgress,
  ResolvedItemInPathStep
} from 'shared-types';

type PathAttributes = Pick<
  Path,
  | 'id'
  | 'title'
  | 'description'
  | 'endDate'
  | 'picture'
  | 'picturePosition'
  | 'startDate'
  | 'userAccess'
>;

export type PathInTile = PathAttributes & {
  categories: string[];
  competencies: CompetencyInProduct[];
  deadlines?: { [stepId: string]: string };
  progressData?: PathStepProgress;
  steps: {
    id: PathStep['id'];
    isMandatory: PathStep['isMandatory'];
    items: ResolvedItemInPathStep[];
    number: PathStep['number'];
  }[];
  userIsEnrolled: boolean;
};

interface Props {
  path: PathInTile;
  pathType: 'available' | 'user';
}

const PathsTile = ({ path, pathType }: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme, user } = useReduxState();

  const handleClick = () => {
    Router.push(`/paths/${path.id}`);
  };
  if (!path) return null;

  return (
    <div className="path-tile" onClick={handleClick}>
      <PathPicture
        picture={path.picture}
        picturePosition={path.picturePosition}
        size={960}
        style={{
          borderRadius: `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`,
          height: 140
        }}
      />

      <div className="content">
        <PathsTileHeader numberOfSteps={path.steps?.length || 0} path={path} />

        {path.description ? (
          <div className="description">{htmlToPlainText(path.description)}</div>
        ) : null}

        <CategoryBadges categories={path.categories} />

        <div className="footer">
          {pathType === 'user' && path.progressData?.pathCompletion ? (
            <div className="progress">
              <CardProgressStatus
                category="success"
                icon="check"
                label={messages.commonCompleted}
              />
            </div>
          ) : pathType === 'user' && path.progressData ? (
            <div className="progress">
              <UserPathStepProgress
                progressData={path.progressData}
                style={{ display: 'block' }}
              />
            </div>
          ) : null}

          <div className="actions">
            {pathType === 'available' &&
            pathCanBeJoined({
              pathEndDate: path.endDate,
              user,
              userAccess: path.userAccess,
              userIsEnrolled: path.userIsEnrolled
            }) ? (
              <PathsTileJoinPath
                pathId={path.id}
                pathTitle={path.title}
                pathAvailable={Date.parse(path.startDate) <= dateNow}
              />
            ) : (
              <LinkButton
                href={`/paths/${path.id}`}
                size="small"
                text={messages.commonOpen}
                {...{ isRounded: true }}
              />
            )}
          </div>
        </div>
      </div>

      {path.steps?.length ? (
        <div className="steps">
          <PathsTileSteps path={path} pathType={pathType} />
        </div>
      ) : null}

      <style jsx>{`
        .path-tile {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.3s;
        }
        .path-tile:focus,
        .path-tile:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .content {
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          min-height: 140px;
          padding: 15px;
        }
        .description {
          color: ${theme.textColorLight};
          display: -webkit-box;
          font-size: 1.4rem;
          margin-top: 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .footer {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: auto;
          padding-top: 30px;
        }
        .progress {
          display: inline-flex;
          width: 100%;
        }
        .steps {
          border-top: 1px solid ${theme.lightGreyDarkActive};
          border-radius: 0 0 ${theme.cardBorderRadius};
          flex-grow: 1;
          overflow-y: scroll;
        }
        @media (min-width: ${breakpoints.small}px) {
          .footer {
            align-items: flex-end;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default PathsTile;
